/**
 * UsageModal Component
 * 
 * A modal showing the user's subscription usage:
 * - Plan name
 * - Messages usage (prompts)
 * - Reset date
 * - Upgrade button
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import back4app2 from '../../back4app2';
import { AgentsSubscriptionInfo } from '@back4app2/sdk';
import LoadingSpinner from '../LoadingSpinner';

interface UsageModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
}

const UsageModal = ({ open, onClose }: UsageModalProps) => {
  const [subscription, setSubscription] = useState<AgentsSubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch subscription data when modal opens
  useEffect(() => {
    if (open) {
      const fetchSubscription = async () => {
        try {
          setLoading(true);
          setError(null);
          const data = await back4app2.getMyAgentsSubscription();
          setSubscription(data);
        } catch (err) {
          console.error('Error fetching agents subscription:', err);
          setError('Failed to load usage data');
        } finally {
          setLoading(false);
        }
      };
      fetchSubscription();
    }
  }, [open]);

  // Calculate usage percentage
  const messagesPercentage = subscription
    ? (subscription.chatMessagesUsage / subscription.chatMessagesHardLimit) * 100
    : 0;

  /**
   * Get color based on usage percentage
   */
  const getBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  /**
   * Format date for display
   */
  const formatResetDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  /**
   * Handle upgrade button click
   */
  const handleUpgradeClick = () => {
    window.open('https://www.back4app.com/pricing/agent', '_blank', 'noopener,noreferrer');
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] animate-slide-in-center"
        style={{ maxWidth: '90vw' }}
      >
        <div className="w-[360px] bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
            <div className="flex items-center gap-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-[#208AEC]">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h2 className="text-lg font-semibold text-white">Usage & Plan</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Close"
            >
              <CloseIcon width="12" height="12" />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner width="32px" height="32px" />
            </div>
          ) : error ? (
            <div className="px-5 py-8 text-center">
              <p className="text-red-400 text-sm">{error}</p>
              <button
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  back4app2.getMyAgentsSubscription()
                    .then(setSubscription)
                    .catch(() => setError('Failed to load usage data'))
                    .finally(() => setLoading(false));
                }}
                className="mt-3 text-sm text-[#208AEC] hover:underline"
              >
                Try again
              </button>
            </div>
          ) : subscription ? (
            <div>
              {/* Plan */}
              <div className="px-5 py-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Plan</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-white">{subscription.planName}</span>
                    <span className={`w-2 h-2 rounded-full ${subscription.planType === 'FREE' ? 'bg-emerald-500' : 'bg-[#208AEC]'}`} />
                  </div>
                </div>
              </div>

              {/* Messages Usage */}
              <div className="px-5 py-4 border-b border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/60">Messages</span>
                  <span className="text-sm text-white/80">
                    {subscription.chatMessagesUsage.toLocaleString()}/{subscription.chatMessagesHardLimit.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${getBarColor(messagesPercentage)} transition-all`}
                    style={{ width: `${Math.min(messagesPercentage, 100)}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-white/40">
                  {Math.round(messagesPercentage)}% used
                </p>
              </div>

              {/* Reset Date */}
              <div className="px-5 py-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Resets</span>
                  <span className="text-sm text-white/80">
                    {formatResetDate(subscription.chatMessagesUsageResetAt)}
                  </span>
                </div>
              </div>

              {/* Pending Payment Warning */}
              {subscription.isPendingPayment && (
                <div className="px-5 py-3 bg-yellow-500/10 border-b border-yellow-500/20">
                  <p className="text-sm text-yellow-400">
                    ⚠️ Payment pending. Please complete payment to continue using the service.
                  </p>
                </div>
              )}

              {/* Upgrade Button */}
              <div className="p-4">
                <button
                  onClick={handleUpgradeClick}
                  className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Upgrade Plan
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>,
    document.body
  );
};

export default UsageModal;

